"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import AppLogo from "@/components/ui/AppLogo";

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const parallaxOffset = scrollY * 0.35;

  return (
    <section
      ref={heroRef}
      className="relative w-full min-h-screen flex items-end overflow-hidden"
      id="hero">

      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 z-0"
        style={{ transform: `translateY(${parallaxOffset}px) scale(1.15)`, transformOrigin: "center top" }}>

        <AppImage
          src="/assets/images/1ef7d464-472c-43a2-b914-184b7c692821-1771809787217.jpg"
          alt="APEX CONSTRUCTION luxury interior renovation project showcase in Hong Kong"
          fill
          priority
          className="object-cover object-center" />

      </div>
      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-10 hero-overlay" />
      {/* Gold diagonal accent */}
      <div
        className="absolute top-0 left-0 w-full h-1 z-20"
        style={{ background: "linear-gradient(90deg, transparent 0%, #D4AF37 30%, #E8CC6A 50%, #D4AF37 70%, transparent 100%)" }} />

      {/* Rotating Badge */}
      <div className="absolute top-28 right-6 md:right-14 z-30 hidden md:block">
        <div className="relative w-28 h-28 flex items-center justify-center">
          <svg className="animate-spin-slow w-full h-full" viewBox="0 0 100 100">
            <defs>
              <path id="badgeCircle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
            </defs>
            <text fontSize="9" fontFamily="DM Sans" fontWeight="700" letterSpacing="3px" fill="#D4AF37">
              <textPath href="#badgeCircle" startOffset="0%">
                APEX CONSTRUCTION • 高峯營造 •
              </textPath>
            </text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full glass-gold flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      {/* Main Hero Content */}
      <div className="relative z-20 w-full max-w-screen-xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">

        {/* Left: Main Text */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          {/* Tag */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-[1px] bg-gold-DEFAULT" style={{ background: "#D4AF37" }} />
            <span
              className="text-xs font-bold uppercase tracking-[0.2em]"
              style={{ color: "#D4AF37" }}>旺角 · MONG KOK · EST. 2025


            </span>
          </div>

          {/* Logo + Brand */}
          <div className="flex items-center gap-4 mb-2">
            <AppLogo
              size={52}
              text="APEX"
              iconName="BuildingOffice2Icon"
              className="" />

            <div>
              <div className="font-display font-bold text-white text-xl leading-tight">APEX CONSTRUCTION</div>
              <div className="text-sm" style={{ color: "rgba(245,240,232,0.65)" }}>高峯營造（建設）有限公司</div>
            </div>
          </div>

          {/* Headline */}
          <h1 className="font-display hero-title text-white leading-none">
            <span className="block">卓越工藝</span>
            <span className="block italic" style={{ color: "#D4AF37" }}>Crafted</span>
            <span className="block">Spaces</span>
          </h1>

          {/* Subhead */}
          <p className="text-base md:text-lg max-w-lg leading-relaxed" style={{ color: "rgba(245,240,232,0.75)" }}>
            香港頂級室內設計與裝修專家 · 由概念到交樓，一手包辦
            <br />
            <span className="text-sm">Hong Kong's premier renovation specialists — from concept to completion.</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mt-2">
            <a
              href="https://wa.me/85256614547"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-pulse flex items-center gap-3 px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #25D366, #128C7E)",
                color: "#fff",
                boxShadow: "0 8px 30px rgba(37,211,102,0.35)"
              }}>

              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp 即時查詢
            </a>
            <a
              href="#services"
              className="flex items-center gap-2 px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 glass-gold"
              style={{ color: "#D4AF37" }}>

              探索服務 Services
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Right: Floating Stat Cards */}
        <div className="lg:col-span-5 flex flex-col gap-4 items-end">
          {/* Card 1 */}
          <div className="animate-float-a glass-navy rounded-2xl p-5 w-full max-w-xs gold-glow">
            <div className="text-xs uppercase tracking-widest mb-1 font-bold" style={{ color: "#D4AF37" }}>
              完工項目 Projects
            </div>
            <div className="font-display text-4xl font-bold text-white">500+</div>
            <div className="text-xs mt-1" style={{ color: "rgba(245,240,232,0.6)" }}>住宅及商業項目</div>
          </div>

          {/* Card 2 */}
          <div className="animate-float-b glass-navy rounded-2xl p-5 w-full max-w-xs" style={{ marginRight: "1.5rem" }}>
            <div className="text-xs uppercase tracking-widest mb-1 font-bold" style={{ color: "#D4AF37" }}>
              客戶滿意度
            </div>
            <div className="flex items-center gap-2">
              <div className="font-display text-4xl font-bold text-white">4.9</div>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5]?.map((i) =>
                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#D4AF37">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                )}
              </div>
            </div>
            <div className="text-xs mt-1" style={{ color: "rgba(245,240,232,0.6)" }}>Google 評分</div>
          </div>

          {/* Card 3 */}
          <div className="animate-float-c glass-navy rounded-2xl p-5 w-full max-w-xs">
            <div className="text-xs uppercase tracking-widest mb-1 font-bold" style={{ color: "#D4AF37" }}>
              地址 Address
            </div>
            <div className="font-bold text-white text-sm leading-snug">旺角上海街426號</div>
            <div className="text-xs mt-0.5" style={{ color: "rgba(245,240,232,0.6)" }}>426 Shanghai St, Mong Kok</div>
          </div>
        </div>
      </div>
      {/* Bottom scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
        <span className="text-xs uppercase tracking-widest" style={{ color: "rgba(245,240,232,0.4)" }}>Scroll</span>
        <div className="w-[1px] h-10" style={{ background: "linear-gradient(to bottom, rgba(212,175,55,0.6), transparent)" }} />
      </div>
    </section>);

}